import { Component, OnInit, OnDestroy, ViewEncapsulation, ViewChild, TemplateRef, signal } from '@angular/core';
import { ClientInformation } from '../../models/client-information';
import { IntegrationMaintenanceService } from '../../services/integration-maintenance.service';
import { ClientIntegrationService } from '../../services/client-integration.service';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '../../models/user';
import { SearchTerm } from 'src/app/models/search-term';
import { IntegrationMaintenance } from 'src/app/models/integration-maintenance';
import { IntegrationMaintenanceFilters } from 'src/app/models/integration-maintenance-filters';
import { Integration } from '../../models/integration';
import { Server } from '../../models/server';
import { DestinationEndpoint } from '../../models/destination-endpoint';
import { ImportType } from '../../models/import-type';
import { ToastService } from '../../services/toast.service';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';

const _flatten = filter => {
  const filters = filter.filters;
  if (filters) {
    return filters.reduce((acc, curr) => acc.concat(curr.filters ? _flatten(curr) : [curr]), []);
  }
  return [];
};

@Component({
  selector: 'app-integration-maintenance',
  templateUrl: './integration-maintenance.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./integration-maintenance.component.scss'],
  standalone: false,
})
export class IntegrationMaintenanceComponent implements OnInit, OnDestroy {
  @ViewChild('maintenanceDialog', { static: true }) maintenanceDialog: TemplateRef<any>;
  componentDestroyed$ = new Subject<boolean>();

  // Constants
  type: 'numeric' | 'input' = 'input';
  allMaintenance: IntegrationMaintenance[];
  filteredMaintenance: IntegrationMaintenance[];

  // Data
  filteredClients: ClientInformation[] = [];
  filteredIntegrations: Integration[] = [];
  selectedClients: ClientInformation[] = [];
  selectedServers: Server[] = [];
  selectedDestinationEndpoints: DestinationEndpoint[] = [];
  selectedImportTypes: ImportType[] = [];
  selectedIntegrations: Integration[] = [];

  allClients: ClientInformation[] = [];
  allIntegrations: Integration[] = [];
  servers: Server[] = [];
  destinationEndpoints: DestinationEndpoint[] = [];
  importTypes: ImportType[] = [];
  maintenance: IntegrationMaintenance;
  dataLoading = true;

  // Dynamic
  maintenanceGridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);
  
  maintenanceDialogObject: MatDialogRef<any, any>;
  aliasSearchTerm: SearchTerm;

  constructor(
    protected maintenanceService: IntegrationMaintenanceService,
    private router: Router,
    private clientService: ClientIntegrationService,
    private typeConstantService: TypeConstantService,
    private user: User,
    private toastService: ToastService,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit(): void {
    this.maintenanceGridSettings.set(this.setupGrid());
    this.getReportConstants();
    this.getClientIntegrationData();
    this.getMaintenance();
  }

  getMaintenance(): void {
    this.maintenanceService.getAllMaintenances()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (m) => {
          if (m != null) {
            this.allMaintenance = m;
            this.allMaintenance.forEach(m => this.setDisplayStrings(m.maintenanceFilters));
            this.filteredMaintenance = this.allMaintenance;
            this.maintenanceGridSettings.set(this.maintenanceGridSettings().setData(this.filteredMaintenance));
          }
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to load Integration Maintenance.', 'Warning', {
            autoClose: false,
          });
        },
        complete: () => {
          this.maintenanceGridSettings().loading.set(false);
        },
      });
    this.aliasSearchTerm = new SearchTerm('', 'Search by Integration Name');
  }

  setDisplayStrings(filters: IntegrationMaintenanceFilters) {
    filters.destinationEndpointsString = JSON.stringify(filters.destinationEndpointIds);
    filters.importTypesString = JSON.stringify(filters.importTypeIds);
    filters.clientInformationsString = JSON.stringify(filters.clientInformationIds);
    filters.serversString = JSON.stringify(filters.serverIds);
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  getReportConstants(): void {
    this.typeConstantService.getAllReportViewConstants()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        (data) => {
          if (data != null) {
            this.destinationEndpoints = data.destinationEndpoints;
            this.importTypes = data.importTypes;
            this.servers = data.servers;
          }
        },
        (_error) => {
          this.toastService.toastCreate('Report Constants failed to return', 'Danger');
        },
      );
  }

  
  setupGrid(): GridSettings {
    return new GridSettings({
      selection: {
        enabled: true,
        selectionType: 'row',
        mode: 'single',
      },
      loading: this.gridLoading,
      dataKey: 'defaultConfigurationPlaceholderId',
      data: this.filteredMaintenance,
      fixedGrid: true,
      columns: [
        new GridColumnData({ filterable: false, field: 'maintenanceStartTime', header: 'Start Time', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ filterable: false, field: 'maintenanceStopTime', header: 'Stop Time', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ width: '150px', field: 'isActive', header: 'Active'}),
        new GridColumnData({ width: '200px', field: 'maintenanceFilters.clientInformationsString', header: 'Client Ids'}),
        new GridColumnData({ width: '200px', field: 'maintenanceFilters.destinationEndpointsString', header: 'Destination Endpoint Ids'}),
        new GridColumnData({ width: '200px', field: 'maintenanceFilters.importTypesString', header: 'Import Type Ids'}),
        new GridColumnData({ width: '150px', field: 'maintenanceFilters.serversString', header: 'Server Ids'}),
        new GridColumnData({ width: '150px', field: 'maintenanceFilters.integrationIds.length', header: 'Integration Count'}),
      ],
      sortMode: 'multiple',
      filter: {
        enabled: true,
        display: 'row',
        mode: 'row',
      },
      multisort: [
        {
          field: 'updatedDate',
          order: -1,
        },
      ],
    });
  }


  getClientIntegrationData(): void {
    forkJoin({
      clients: this.clientService.getAllClients(),
      integrations: this.clientService.getAllNotDeletedIntegrations(),
    })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(({ clients, integrations }) => {
        this.allClients = clients;
        this.filteredClients = clients;
        this.allIntegrations = integrations;
        this.filteredIntegrations = integrations;
        this.dataLoading = false;
      },
        (_error) => {
          this.toastService.toastCreate('Clients/Integrations failed to return', 'Danger');
          this.dataLoading = false;
        });
  }

  addMaintenance(): void {
    this.maintenance = null;
    if (!this.dataLoading) {
      this.maintenanceDialogObject = this.dialog.open(this.maintenanceDialog, { height: '90vh', width: '70vw' });
    }
  }

  selectionChangeHandler(event: IntegrationMaintenance[]): void {
    this.maintenance = event[0];
    if (!this.dataLoading) {
      this.maintenanceDialogObject = this.dialog.open(this.maintenanceDialog, { height: '90vh', width: '70vw' });
    }
  }

  updateMaintenance(event: IntegrationMaintenance): void {
    let updatedMaintenance = false;
    this.setDisplayStrings(event.maintenanceFilters);
    for (let i = 0; i < this.allMaintenance.length; i++) {
      if (this.allMaintenance[i].integrationMaintenanceId == event.integrationMaintenanceId) {
        this.allMaintenance[i] = event;
        updatedMaintenance = true;
        break;
      }
    }
    if (!updatedMaintenance) {
      this.allMaintenance.push(event);
    }
    this.dialog.closeAll();
    this.filterMaintenance();
  }

  deleteMaintenance(event: IntegrationMaintenance): void {
    for (let i = 0; i < this.allMaintenance.length; i++) {
      if (this.allMaintenance[i].integrationMaintenanceId == event.integrationMaintenanceId) {
        this.allMaintenance.splice(i, 1);
        break;
      }
    }
    this.dialog.closeAll();
    this.filterMaintenance();
  }

  filterClients(searchTerm: string): void {
    this.filteredClients = this.allClients.filter(c => c.clientDisplayName.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  filterIntegrations(searchTerm: string): void {
    this.filteredIntegrations = this.allIntegrations.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  filterMaintenance(): void {
    this.filteredMaintenance = this.allMaintenance;

    // Clients
    if (this.selectedClients.length > 0) {
      const clientIds = this.selectedClients.map(c => c.clientInformationId);
      this.filteredMaintenance = this.filteredMaintenance.filter(m => clientIds.some(c => m.maintenanceFilters.clientInformationIds.includes(c)));
    }

    // Destination Endpoint
    if (this.selectedDestinationEndpoints.length > 0) {
      const destIds = this.selectedDestinationEndpoints.map(d => d.destinationEndpointId);
      this.filteredMaintenance = this.filteredMaintenance.filter(m => destIds.some(d => m.maintenanceFilters.destinationEndpointIds.includes(d)));
    }

    // Import Type
    if (this.selectedImportTypes.length > 0) {
      const importIds = this.selectedImportTypes.map(i => i.importTypeId);
      this.filteredMaintenance = this.filteredMaintenance.filter(m => importIds.some(i => m.maintenanceFilters.importTypeIds.includes(i)));
    }

    // Server
    if (this.selectedServers.length > 0) {
      const serverIds = this.selectedServers.map(s => s.serverId);
      this.filteredMaintenance = this.filteredMaintenance.filter(m => serverIds.some(s => m.maintenanceFilters.serverIds.includes(s)));
    }

    // Server
    if (this.selectedIntegrations.length > 0) {
      const integrationIds = this.selectedIntegrations.map(i => i.integrationId);
      this.filteredMaintenance = this.filteredMaintenance.filter(m => integrationIds.some(i => m.maintenanceFilters.integrationIds.includes(i)));
    }

    this.maintenanceGridSettings.set(this.maintenanceGridSettings().setData(this.filteredMaintenance));
  }

  checkAccess(role: string[]): boolean {
    if (this.user.checkAccess(role)) {
      return true;
    } else {
      return false;
    }
  }
}
